import {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import styles from "./GameScreen.module.scss";

import {getGame} from "../../../api/game";
import {useGameSocket} from "../../../api/ws/useGameSocket";
import {AckCode, AckEvent, GameEvent} from "../../../api/ws/events";
import {Game, GameState, isGameAvailable, OPPONENT_SEAT_ORDER, reduceGame, UserAction} from "../../types/game";
import {toNum} from "../../utils";
import PlayerSeat from "./GameComponents/PlayerSeat";
import DeckSlot from "./GameComponents/DeckSlot";
import GameScreenHeader from "./GameComponents/GameScreenHeader";
import {getCurrentUserId, getCurrentUsername} from "../../../api/authContext";
import {getLobby} from "../../../api/lobby";
import {Lobby} from "../../types/lobby";
import {Flipper} from "react-flip-toolkit";

const GameScreen = () => {
    const gameId = toNum(useParams()["gameId"]);
    const navigate = useNavigate();

    const [game, setGame] = useState<Game | null>(null);
    const [lobby, setLobby] = useState<Lobby | null>(null);
    const gameVersionRef = useRef<number>(0);
    const syncRef = useRef<(gameVersion: number) => void>(() => {
    });

    const onGameEvent = useCallback((event: GameEvent) => {
        console.log("GameEvent", event);

        const eventVersion = event.version;
        const currentVersion = gameVersionRef.current;

        if (event.type === 'GAME_STATE' && eventVersion >= currentVersion) {
            console.log(`Apply game state event: ${currentVersion} -> ${eventVersion}`);
            setGame(prev => reduceGame(prev, event));

            gameVersionRef.current = eventVersion;

            if (!isGameAvailable(event.game.state)) {
                navigate(`/lobby/${event.game.lobbyId}`);
            }
            return;
        }

        if (eventVersion <= currentVersion) {
            console.log("Received old game event", event.version);
            return;
        }

        if (eventVersion === currentVersion + 1) {
            console.log(`Update game state : ${currentVersion} -> ${eventVersion}`);

            setGame(prev => reduceGame(prev, event));
            gameVersionRef.current = eventVersion;
            return;
        }

        console.warn('GAP in game versions, need sync', {currentVersion, got: eventVersion});
        syncRef.current(gameVersionRef.current);
    }, []);

    const onAck = useCallback((ack: AckEvent) => {
        if (ack.type === "ERROR") {
            console.error("[GAME ACK ERROR]", ack.code, ack.message);

            if (ack.code === AckCode.SYNC_NEEDED) {
                syncRef.current(gameVersionRef.current);
            }

            return;
        }

        if (ack.type === "OK") {
            console.log("[GAME ACK OK]", ack.requestId);
            return;
        }

        console.log("[GAME ACK OTHER]", ack);
    }, []);

    const {connect, sync, leave, sendCardEvent, sendSwapEvent, sendKnockEvent, sendEndGameEvent} = useGameSocket({
        gameId,
        onGameEvent,
        onAck,
    });

    useEffect(() => {
        syncRef.current = sync;
    }, [sync]);

    useEffect(() => {
        if (Number.isNaN(gameId)) {
            navigate("/lobby");
            return;
        }

        getGame(gameId)
            .then(game => {
                setGame(game);
                if (gameVersionRef.current < game.version) {
                    gameVersionRef.current = game.version;
                }

                if (isGameAvailable(game.state)) {
                    connect(gameVersionRef.current);
                } else {
                    navigate(`/lobby/${game.lobbyId}`);
                }

                return game;
            })
            .then(game => getLobby(game.lobbyId, false))
            .then(lobby => setLobby(lobby))
            .catch(() => navigate("/lobby"));
    }, [gameId, navigate]);

    const handleLeave = () => {
        leave();
        navigate("/lobby");
    };

    const username = getCurrentUsername();
    const currentUserId = getCurrentUserId();
    const currentPlayer = game?.users?.find(u => u.userId === game?.gameData?.currentPlayerId);

    if (!game || !lobby || !currentPlayer || !username || Number.isNaN(currentUserId)) {
        return (
            <div className={styles.gameScreen__loading}>Loading game...</div>
        );
    }

    const {gameData} = game;

    const isYourTurn = gameData.currentPlayerId === currentUserId;

    const handlePlayerSeatClick = (playerId: number) => {
        if (currentPlayer.gameUserData.actions.requiredAction !== UserAction.FORCE_CARD_TAKE) {
            return;
        }

        sendCardEvent(
            gameVersionRef.current,
            UserAction.FORCE_CARD_TAKE,
            playerId,
        );

        console.log("Player seat clicked", playerId);
    };

    const handleCardClick = (playerId: number, cardSlot: number) => {
        console.log("Card clicked", playerId, cardSlot);

        if (!currentPlayer.gameUserData.actions.requiredAction) {
            console.log("No active actions for player", currentPlayer.userId);
            return;
        }

        sendCardEvent(
            gameVersionRef.current,
            currentPlayer.gameUserData.actions.requiredAction,
            playerId,
            cardSlot,
        );
    };

    const handleCardRightClick = (playerId: number, cardSlot: number, userAction: UserAction) => {
        sendCardEvent(
            gameVersionRef.current,
            userAction,
            playerId,
            cardSlot,
        );
    };

    const handleSpecialAction = (playerId: number, swap?: boolean, knock?: boolean) => {
        if (typeof swap === 'boolean') {
            sendSwapEvent(gameVersionRef.current, playerId, swap);
            return;
        }

        if (typeof knock === 'boolean') {
            sendKnockEvent(gameVersionRef.current, playerId);
        }
    }

    const handleEndGameEvent = () => {
        if (game.state === GameState.RESULTS_REVEAL) {
            sendEndGameEvent(gameVersionRef.current);
        }
    }

    const otherPlayers = game.users
        .filter(u => u.userId !== currentUserId)
        .sort((u1, u2) => u2.turnOrder - u1.turnOrder);

    let opponentSeats = OPPONENT_SEAT_ORDER[game.users.length];

    const seatsForOpponents: { playerId: number; pos: string }[] =
        otherPlayers.slice(0, opponentSeats.length).map((p, idx) => ({
            playerId: p.userId,
            pos: opponentSeats[idx],
        }));

    const isOpponentSeatClickable = isYourTurn && currentPlayer.gameUserData.actions.requiredAction === UserAction.FORCE_CARD_TAKE;

    return (
        <div className={styles.gameScreen}>
            <GameScreenHeader
                game={game}
                lobby={lobby}
                yourTurn={isYourTurn}
                currentPlayerUserName={currentPlayer?.user.username}
                handleLeave={handleLeave}
            />

            <Flipper className={styles.gameScreen__tableLayout} flipKey={gameVersionRef.current}>
                <DeckSlot
                    game={game}
                    size={'md'}
                    playerId={currentUserId}
                    onDeckCardClick={handleCardClick}
                    onReturnToLobby={handleEndGameEvent}
                />

                {<PlayerSeat
                    key={"player-seat-" + currentUserId}
                    game={game}
                    playerId={currentUserId}
                    isYou={true}
                    playerSeatIsClickable={false}
                    position={'bottom'}
                    onPlayerSeatClick={handlePlayerSeatClick}
                    onCardSlotClick={handleCardClick}
                    onCardSlotRightClick={handleCardRightClick}
                    onSpecialAction={handleSpecialAction}
                />}

                {seatsForOpponents.map(({playerId, pos}) => {
                        return <PlayerSeat
                            key={"player-seat-" + playerId}
                            game={game}
                            playerId={playerId}
                            isYou={false}
                            playerSeatIsClickable={isOpponentSeatClickable}
                            position={pos}
                            onPlayerSeatClick={handlePlayerSeatClick}
                            onCardSlotClick={handleCardClick}
                            onCardSlotRightClick={handleCardRightClick}
                            onSpecialAction={handleSpecialAction}
                        />;
                    }
                )}
            </Flipper>
        </div>
    );
};

export default GameScreen;
